import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  UserMinus, Plus, MoreVertical, Search, Filter, Eye, FileText, CheckCircle2,
  FileDown, ClipboardCheck, History as HistoryIcon, Star, TrendingUp, Clock,
} from "lucide-react";
import { API_BASE } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";

const STATUS = {
  solicitado:              { label: "Solicitado", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  em_andamento:            { label: "Em andamento", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  documentacao_pendente:   { label: "Doc. pendente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  aguardando_exame:        { label: "Aguardando exame", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  aguardando_assinatura:   { label: "Aguardando assinatura", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  concluido:               { label: "Concluído", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelado:               { label: "Cancelado", cls: "bg-red-100 text-red-700 border-red-200" },
};

const TIPOS = {
  demissao_empresa: "Demissão pela empresa",
  pedido_demissao:  "Pedido de demissão",
  acordo:           "Acordo",
  justa_causa:      "Justa causa",
};

const empty = {
  employee_id: "", tipo: "demissao_empresa", motivo: "",
  data_desligamento: "", data_aviso_previo: "",
  aviso_tipo: "indenizado", entrevista_desligamento: "",
  observacoes: "", status: "solicitado",
};

export default function TerminationsPage() {
  const { companyParam } = useCompany();
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin", "rh_operacional");
  const [list, setList] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [employees, setEmployees] = useState([]);

  // Detail + checklist
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  // Exit interview + timeline
  const [eiOpen, setEiOpen] = useState(false);
  const emptyEi = {
    avaliacao_gestao: 0, avaliacao_ambiente: 0, avaliacao_remuneracao: 0,
    avaliacao_beneficios: 0, avaliacao_carreira: 0, avaliacao_treinamento: 0,
    avaliacao_ferramentas: 0, avaliacao_geral: 0,
    nps_recomendaria: 0, motivo_principal_saida: "", pontos_positivos: "",
    pontos_melhoria: "", sugestoes: "", destino: "nova_empresa",
    destino_detalhe: "", realizada_por: "",
    data_entrevista: new Date().toISOString().slice(0, 10),
  };
  const [eiForm, setEiForm] = useState(emptyEi);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    api.get("/employees", { params: {
      page_size: 500, status: "ativo",
      ...(companyParam ? { company_id: companyParam } : {}),
    }}).then(({ data }) => setEmployees(data.items || []));
  }, [companyParam]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const [t, d] = await Promise.all([
        api.get("/terminations", { params }),
        api.get("/terminations/dashboard", { params: companyParam ? { company_id: companyParam } : {} }),
      ]);
      setList(t.data); setDash(d.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyParam, statusFilter]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openNew = () => { setEditing(null); setForm({ ...empty, data_desligamento: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t }); setOpen(true); };

  const save = async () => {
    if (!form.employee_id && !editing) { toast.error("Selecione o colaborador"); return; }
    if (!form.motivo?.trim()) { toast.error("Informe o motivo"); return; }
    try {
      if (editing) await api.put(`/terminations/${editing.id}`, form);
      else await api.post("/terminations", form);
      toast.success("Processo salvo");
      setOpen(false); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const openDetail = async (t) => {
    try {
      const { data } = await api.get(`/terminations/${t.id}`);
      setDetail(data); setDetailOpen(true);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const toggleChecklist = async (itemId, done) => {
    try {
      await api.put(`/terminations/${detail.id}/checklist/${itemId}`, { done, label: "" });
      const { data } = await api.get(`/terminations/${detail.id}`);
      setDetail(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const changeStatus = async (t, status) => {
    try {
      await api.put(`/terminations/${t.id}`, { ...t, status });
      toast.success(`Status → ${STATUS[status].label}`);
      load();
      if (detailOpen && detail?.id === t.id) {
        const { data } = await api.get(`/terminations/${t.id}`);
        setDetail(data);
      }
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const checklistProgress = useMemo(() => {
    if (!detail?.checklist?.length) return 0;
    const done = detail.checklist.filter(i => i.done).length;
    return Math.round((done / detail.checklist.length) * 100);
  }, [detail]);

  // ---- Exit Interview ----
  const openExitInterview = () => {
    if (detail?.exit_interview) {
      setEiForm({ ...emptyEi, ...detail.exit_interview });
    } else {
      setEiForm({ ...emptyEi });
    }
    setEiOpen(true);
  };
  const saveExitInterview = async () => {
    try {
      await api.post(`/terminations/${detail.id}/exit-interview`, {
        termination_id: detail.id,
        ...eiForm,
      });
      toast.success("Entrevista de desligamento salva");
      setEiOpen(false);
      const { data } = await api.get(`/terminations/${detail.id}`);
      setDetail(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // ---- Timeline ----
  const openTimeline = async () => {
    setTimeline(null);
    setTimelineOpen(true);
    try {
      const { data } = await api.get(`/terminations/${detail.id}/timeline`);
      setTimeline(data);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  // ---- Export Excel ----
  const exportExcel = async () => {
    try {
      const token = localStorage.getItem("rh_token");
      const url = `${API_BASE}/terminations/export-excel${companyParam ? `?company_id=${companyParam}` : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "desligamentos.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Falha ao exportar"); }
  };

  const topMotivos = Object.entries(dash?.por_motivo || {}).slice(0, 5);
  const topSetores = Object.entries(dash?.por_setor || {}).slice(0, 5);

  return (
    <div className="space-y-6" data-testid="terminations-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Movimentações</div>
        <h1 className="font-display text-3xl font-semibold">Desligamentos</h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">Processo completo com checklist, aviso prévio, documentos e histórico.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <K label="Total de processos" value={dash?.totals?.total} tone="primary" />
        <K label="Em andamento" value={dash?.totals?.em_andamento} tone="warning" />
        <K label="Concluídos" value={dash?.totals?.concluidos} tone="success" />
        <K label="Pedidos demissão" value={dash?.totals?.pedidos_demissao} tone="accent" />
        <K label="Demissões empresa" value={dash?.totals?.demissoes_empresa} />
        <K label="Turnover 12m" value={dash?.totals ? `${dash.totals.turnover_pct}%` : "—"} icon={TrendingUp} tone="danger" />
        <K label="Tempo médio (d)" value={dash?.totals?.tempo_medio_dias} icon={Clock} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-5">
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Top motivos</div>
          {topMotivos.length === 0 ? <div className="text-[12.5px] text-muted-foreground">Sem dados ainda.</div> :
           topMotivos.map(([m, n]) => (
            <div key={m} className="flex items-center justify-between py-1 text-[13px]">
              <span>{m}</span><Badge variant="outline">{n}</Badge>
            </div>
           ))}
        </div>
        <div className="rounded-lg border border-border bg-white p-5">
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Ranking por setor</div>
          {topSetores.length === 0 ? <div className="text-[12.5px] text-muted-foreground">Sem dados ainda.</div> :
           topSetores.map(([s, n]) => (
            <div key={s} className="flex items-center justify-between py-1 text-[13px]">
              <span>{s}</span><Badge variant="outline">{n}</Badge>
            </div>
           ))}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-border bg-white p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por nome ou matrícula..." value={search}
                 onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="dem-search" />
        </div>
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            {Object.keys(STATUS).map(k => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}
          </SelectContent>
        </Select>
        {canEdit && <Button onClick={openNew} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="dem-new-btn"><Plus className="h-4 w-4 mr-2" /> Abrir processo</Button>}
        <Button variant="outline" onClick={exportExcel} data-testid="dem-export-btn">
          <FileDown className="h-3.5 w-3.5 mr-1.5" /> Exportar Excel
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Colaborador</TableHead>
              <TableHead>Setor</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead>Desligamento</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              if (loading) {
                return [1,2,3].map(i => (
                  <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-5" /></TableCell></TableRow>
                ));
              }
              if (list.length === 0) {
                return (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Nenhum processo aberto.</TableCell>
                  </TableRow>
                );
              }
              return list.map((t) => {
              const st = STATUS[t.status] || STATUS.solicitado;
              return (
                <TableRow key={t.id} className="rh-row" data-testid={`dem-row-${t.id}`}>
                  <TableCell>
                    <div className="font-medium">{t.nome}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{t.matricula}</div>
                  </TableCell>
                  <TableCell className="text-[12.5px]">{t.sector_name || "—"}</TableCell>
                  <TableCell className="text-[12.5px]">{TIPOS[t.tipo] || t.tipo}</TableCell>
                  <TableCell className="text-[12.5px] max-w-[200px] truncate" title={t.motivo}>{t.motivo || "—"}</TableCell>
                  <TableCell className="text-[12.5px]">{fmtDate(t.data_desligamento)}</TableCell>
                  <TableCell><Badge variant="outline" className={`ring-1 ring-inset ${st.cls}`}>{st.label}</Badge></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => openDetail(t)}><Eye className="h-3.5 w-3.5 mr-2" /> Abrir processo</DropdownMenuItem>
                        {canEdit && <DropdownMenuItem onSelect={() => openEdit(t)}>Editar</DropdownMenuItem>}
                        {canEdit && Object.keys(STATUS).filter(s => s !== t.status).map(s => (
                          <DropdownMenuItem key={s} onSelect={() => changeStatus(t, s)}>→ {STATUS[s].label}</DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
             });
            })()}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar processo" : "Abrir processo de desligamento"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {!editing && (
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider">Colaborador</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="dem-emp"><SelectValue placeholder="Selecione o colaborador" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.nome} · {e.matricula} · {e.cargo}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(TIPOS).map(k => <SelectItem key={k} value={k}>{TIPOS[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Aviso prévio</Label>
              <Select value={form.aviso_tipo} onValueChange={(v) => setForm({ ...form, aviso_tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trabalhado">Trabalhado</SelectItem>
                  <SelectItem value="indenizado">Indenizado</SelectItem>
                  <SelectItem value="dispensado">Dispensado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <F label="Data do desligamento" v={form.data_desligamento} s={(v) => setForm({ ...form, data_desligamento: v })} type="date" />
            <F label="Data aviso prévio" v={form.data_aviso_previo} s={(v) => setForm({ ...form, data_aviso_previo: v })} type="date" />
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Motivo</Label>
              <Textarea rows={2} value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="dem-motivo" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Entrevista de desligamento</Label>
              <Textarea rows={2} value={form.entrevista_desligamento || ""} onChange={(e) => setForm({ ...form, entrevista_desligamento: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Observações</Label>
              <Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} data-testid="dem-save-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process detail + checklist */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{detail?.employee?.nome || "Processo"}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={`ring-1 ring-inset ${STATUS[detail.status]?.cls}`}>{STATUS[detail.status]?.label}</Badge>
                <Badge variant="outline">{TIPOS[detail.tipo]}</Badge>
                <Badge variant="outline">Aviso: {detail.aviso_tipo}</Badge>
                <Badge variant="outline">Desligamento: {fmtDate(detail.data_desligamento)}</Badge>
                <Badge variant="outline" className="bg-[#F9F4E7] text-[#8A6B2B] border-[#E8D9AE]">
                  Checklist: {detail.checklist?.filter(i => i.done).length || 0}/{detail.checklist?.length || 0} ({checklistProgress}%)
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><Label className="text-[10.5px] uppercase">Matrícula</Label><div className="font-mono">{detail.employee?.matricula}</div></div>
                <div><Label className="text-[10.5px] uppercase">Cargo</Label><div>{detail.employee?.cargo}</div></div>
              </div>

              {detail.motivo && <div className="rounded-md bg-secondary/40 p-3 text-[13px]"><b className="mr-1">Motivo:</b> {detail.motivo}</div>}

              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium mb-2 flex items-center gap-1">
                  <FileText className="h-3 w-3" /> Checklist rescisório
                </div>
                <div className="space-y-2">
                  {detail.checklist.map((it) => (
                    <div key={it.id} className={`flex items-center gap-3 rounded-md border border-border px-3 py-2 ${it.done ? "bg-emerald-50/60" : ""}`}>
                      <Checkbox checked={it.done} onCheckedChange={(v) => toggleChecklist(it.id, !!v)} disabled={!canEdit} data-testid={`chk-${it.id}`} />
                      <div className="flex-1">
                        <div className={`text-[13px] ${it.done ? "text-emerald-800 line-through" : ""}`}>{it.label}</div>
                        {it.done && (it.completed_by || it.completed_at) && (
                          <div className="text-[10.5px] text-muted-foreground">
                            {it.completed_by || "—"} · {fmtDateTime(it.completed_at)}
                          </div>
                        )}
                      </div>
                      {it.done && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={openExitInterview} data-testid="ei-open-btn" className="bg-[#F9F4E7] border-[#E8D9AE] text-[#8A6B2B]">
                    <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />
                    {detail.exit_interview ? "Editar entrevista estruturada" : "Registrar entrevista estruturada"}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={openTimeline} data-testid="tl-open-btn">
                  <HistoryIcon className="h-3.5 w-3.5 mr-1.5" /> Timeline
                </Button>
                {detail.exit_interview && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                    <Star className="h-3 w-3 mr-1" /> NPS {detail.exit_interview.nps_recomendaria ?? "—"} · Geral {detail.exit_interview.avaliacao_geral ?? "—"}/5
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {canEdit && Object.keys(STATUS).filter(s => s !== detail.status).map(s => (
                  <Button key={s} size="sm" variant="outline" onClick={() => changeStatus(detail, s)}>→ {STATUS[s].label}</Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Exit Interview Dialog */}
      <Dialog open={eiOpen} onOpenChange={setEiOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ei-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#8A6B2B]" /> Entrevista de desligamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium mb-2">
                Avaliação de cada dimensão (1 = muito ruim · 5 = excelente)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  ["avaliacao_gestao", "Gestão direta / liderança"],
                  ["avaliacao_ambiente", "Ambiente de trabalho"],
                  ["avaliacao_remuneracao", "Remuneração"],
                  ["avaliacao_beneficios", "Benefícios"],
                  ["avaliacao_carreira", "Plano de carreira"],
                  ["avaliacao_treinamento", "Treinamento e desenvolvimento"],
                  ["avaliacao_ferramentas", "Ferramentas e recursos"],
                  ["avaliacao_geral", "Avaliação geral da empresa"],
                ].map(([key, label]) => (
                  <StarRating key={key} label={label} value={eiForm[key] || 0}
                              onChange={(n) => setEiForm({ ...eiForm, [key]: n })}
                              testid={`ei-${key}`} />
                ))}
              </div>
            </div>

            <div className="rounded-md border border-border p-3">
              <Label className="text-[11px] uppercase tracking-wider">
                NPS · Recomendaria a empresa a um amigo? (0 a 10)
              </Label>
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                {Array.from({ length: 11 }).map((_, n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`nps-${n}`}
                    onClick={() => setEiForm({ ...eiForm, nps_recomendaria: n })}
                    className={`h-8 w-8 rounded-md text-[12px] font-mono tabular-nums border transition-colors ${(() => {
                      if (eiForm.nps_recomendaria !== n) return "bg-white hover:bg-secondary border-border";
                      if (n >= 9) return "bg-emerald-600 text-white border-emerald-700";
                      if (n >= 7) return "bg-amber-500 text-white border-amber-600";
                      return "bg-red-600 text-white border-red-700";
                    })()}`}
                  >{n}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Motivo principal da saída</Label>
                <Input value={eiForm.motivo_principal_saida}
                       onChange={(e) => setEiForm({ ...eiForm, motivo_principal_saida: e.target.value })}
                       data-testid="ei-motivo" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Destino</Label>
                <Select value={eiForm.destino} onValueChange={(v) => setEiForm({ ...eiForm, destino: v })}>
                  <SelectTrigger data-testid="ei-destino"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nova_empresa">Nova empresa</SelectItem>
                    <SelectItem value="estudos">Estudos / Qualificação</SelectItem>
                    <SelectItem value="empreender">Empreender</SelectItem>
                    <SelectItem value="descanso">Descanso / Sabática</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider">Pontos positivos</Label>
                <Textarea rows={2} value={eiForm.pontos_positivos}
                          onChange={(e) => setEiForm({ ...eiForm, pontos_positivos: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider">Pontos a melhorar</Label>
                <Textarea rows={2} value={eiForm.pontos_melhoria}
                          onChange={(e) => setEiForm({ ...eiForm, pontos_melhoria: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-wider">Sugestões</Label>
                <Textarea rows={2} value={eiForm.sugestoes}
                          onChange={(e) => setEiForm({ ...eiForm, sugestoes: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Realizada por</Label>
                <Input value={eiForm.realizada_por}
                       onChange={(e) => setEiForm({ ...eiForm, realizada_por: e.target.value })} />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Data da entrevista</Label>
                <DateInputBR value={eiForm.data_entrevista}
                       onChange={(v) => setEiForm({ ...eiForm, data_entrevista: v })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEiOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={saveExitInterview} data-testid="ei-save-btn">
              Salvar entrevista
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={timelineOpen} onOpenChange={setTimelineOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tl-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <HistoryIcon className="h-5 w-5" /> Timeline do processo
            </DialogTitle>
          </DialogHeader>
          {!timeline ? (
            <div className="space-y-2"><Skeleton className="h-6 w-3/4" /><Skeleton className="h-6 w-full" /></div>
          ) : (
            <div className="relative pl-7">
              <div className="absolute left-3 top-1 bottom-1 w-px bg-border" aria-hidden />
              {timeline.events.length === 0 && <div className="text-[12.5px] text-muted-foreground">Sem eventos ainda.</div>}
              {timeline.events.map((ev, i) => {
                const getDotColor = () => {
                  if (ev.kind === "entrevista") return "bg-[#8A6B2B]";
                  if (ev.kind === "checklist") return "bg-emerald-600";
                  if (ev.kind === "status") return "bg-blue-600";
                  return "bg-slate-500";
                };
                const dot = getDotColor();
                return (
                  <div key={i} className="relative pb-5 last:pb-0">
                    <span className={`absolute -left-[18px] top-1 h-3 w-3 rounded-full ring-4 ring-white ${dot}`} />
                    <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                      {fmtDateTime(ev.at)} {ev.by && `· ${ev.by}`}
                    </div>
                    <div className="text-[13px] font-medium mt-0.5">{ev.title}</div>
                    {ev.detail && <div className="text-[12px] text-muted-foreground mt-0.5">{ev.detail}</div>}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter><Button onClick={() => setTimelineOpen(false)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StarRating({ label, value, onChange, testid }) {
  return (
    <div className="rounded-md border border-border px-3 py-2 bg-white">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-medium text-foreground/90">{label}</span>
        <div className="flex items-center gap-0.5" data-testid={testid}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className="p-0.5"
              aria-label={`Nota ${n}`}
            >
              <Star
                className={`h-[18px] w-[18px] transition-colors ${
                  n <= value ? "fill-[#C5A059] text-[#C5A059]" : "text-muted-foreground/40 hover:text-[#C5A059]/70"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
import DateInputBR from "@/components/DateInputBR";

const F = ({ label, v, s, type = "text" }) => (
  <div><Label className="text-[11px] uppercase tracking-wider">{label}</Label>
    {type === "date"
      ? <DateInputBR value={v} onChange={s} />
      : <Input type={type} value={v ?? ""} onChange={(e) => s(e.target.value)} />}</div>
);
function K({ label, value, tone = "default", icon: Icon }) {
  const tones = { default: "bg-white", primary: "bg-[#234935] text-white", success: "bg-emerald-50", warning: "bg-amber-50", accent: "bg-[#F9F4E7]", danger: "bg-red-50" };
  const labelColor = tone === "primary" ? "text-white/70" : "text-muted-foreground";
  const valueColor = tone === "primary" ? "text-white" : "text-foreground";
  return (
    <div className={`rounded-lg border border-border ${tones[tone]} p-4`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${labelColor}`}>{label}</div>
          <div className={`mt-1.5 font-display text-[22px] font-semibold ${valueColor}`}>{value ?? 0}</div>
        </div>
        {Icon && <Icon className={`h-4 w-4 shrink-0 ${tone === "primary" ? "text-[#C5A059]" : tone === "danger" ? "text-red-600" : "text-muted-foreground"}`} />}
      </div>
    </div>
  );
}
